import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://bpphmdademo.alliantprojects.com/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  console.log('Bookings API Request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    params: config.params,
    hasToken: !!token,
  });
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('Bookings API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('Bookings API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url
    });
    
    // Don't redirect to login for 401 on public endpoints
    const isPublicEndpoint = error.config?.url?.includes('blocked-dates') || 
                            error.config?.url?.includes('booking-number') ||
                            error.config?.url?.includes('/public') ||
                            error.config?.url?.includes('check-availability') ||
                            error.config?.url?.includes('track');
    
    if (error.response?.status === 401 && !isPublicEndpoint) {
      console.error('401 Unauthorized - Token may be expired or invalid');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Booking {
  id: string;
  bookingNumber: string;
  venueId: string;
  userId: string;
  applicantName: string;
  email: string;
  mobile: string;
  eventName: string;
  bookedFrom: string;
  bookedTo: string;
  prefixDays: number;
  suffixDays: number;
  rentAmount: number;
  securityDeposit: number;
  gst: number;
  totalAmount: number;
  gstNumber?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  bookingDate: string;
  notes?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  venue?: any;
  user?: any;
  payment?: any;
}

export interface CreateBookingData {
  venueId: string;
  applicantName: string;
  email: string;
  mobile: string;
  eventName: string;
  bookedFrom: string;
  bookedTo: string;
  prefixDays?: number;
  suffixDays?: number;
  rentAmount: number;
  securityDeposit: number;
  gst: number;
  totalAmount: number;
  gstNumber?: string;
  notes?: string;
  userId?: string;
}

export interface BookingFilters {
  venueId?: string;
  userId?: string;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  startDate?: string;
  endDate?: string;
  search?: string;
}

class BookingsAPI {
  /**
   * Create new PUBLIC booking (no authentication required)
   * This is for citizen services - public venue booking
   */
  async createPublicBooking(data: CreateBookingData): Promise<Booking> {
    console.log('Creating public booking:', data);
    const response = await api.post('/bookings/public', data);
    console.log('Public booking response:', response.data);
    return response.data.data; // Extract data from wrapped response
  }

  /**
   * Create new AUTHENTICATED booking (requires JWT token)
   * This is for logged-in users
   */
  async createBooking(data: CreateBookingData): Promise<Booking> {
    console.log('Creating authenticated booking:', data);
    const response = await api.post('/bookings', data);
    console.log('Authenticated booking response:', response.data);
    return response.data.data; // Extract data from wrapped response
  }

  /**
   * Get blocked dates for a venue in a date range
   * This is a PUBLIC endpoint - no auth required
   */
  async getBlockedDates(
    venueId: string,
    startDate: string,
    endDate: string
  ): Promise<string[]> {
    try {
      console.log('Fetching blocked dates:', { venueId, startDate, endDate });
      
      const response = await api.get(`/bookings/venue/${venueId}/blocked-dates`, {
        params: { startDate, endDate },
      });
      
      console.log('Blocked dates response:', response.data);
      
      // Handle wrapped response
      if (response.data.success && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      return [];
    }
  }

  /**
   * Check venue availability for specific dates
   * PUBLIC endpoint
   */
  async checkAvailability(
    venueId: string,
    from: string,
    to: string
  ): Promise<boolean> {
    try {
      console.log('Checking availability:', { venueId, from, to });
      
      const response = await api.get(`/bookings/venue/${venueId}/check-availability`, {
        params: { from, to },
      });
      
      console.log('Availability response:', response.data);
      
      return response.data.data?.available || false;
    } catch (error) {
      console.error('Error checking availability:', error);
      return false;
    }
  }

  /**
   * Track booking by booking number and mobile
   * PUBLIC endpoint for citizens to track their bookings
   */
  async trackBooking(bookingNumber: string, mobile: string): Promise<Booking | null> {
    try {
      console.log('Tracking booking:', { bookingNumber, mobile });
      
      const response = await api.get('bookings/track', {
        params: { bookingNumber, mobile },
      });
      
      console.log('Track booking response:', response.data);
      
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error tracking booking:', error);
      return null;
    }
  }

  /**
   * Find booking by booking number (public endpoint for tracking)
   */
  async findByBookingNumber(bookingNumber: string): Promise<Booking> {
    const response = await api.get(`bookings/booking-number/${bookingNumber}`);
    return response.data.data || response.data;
  }

  /**
   * Get user's own bookings (requires auth)
   */
  async getMyBookings(): Promise<Booking[]> {
    const response = await api.get('bookings/my-bookings');
    return response.data.data || response.data;
  }

  /**
   * Get all bookings (admin only)
   */
  async getAllBookings(filters?: BookingFilters): Promise<Booking[]> {
    const params = new URLSearchParams();
    if (filters?.venueId) params.append('venueId', filters.venueId);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`bookings?${params.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get booking by ID (requires auth)
   */
  async getBooking(bookingId: string): Promise<Booking> {
    const response = await api.get(`bookings/${bookingId}`);
    return response.data.data || response.data;
  }

  /**
   * Update booking (requires auth)
   */
  async updateBooking(bookingId: string, data: Partial<CreateBookingData>): Promise<Booking> {
    const response = await api.patch(`bookings/${bookingId}`, data);
    return response.data.data || response.data;
  }

  /**
   * Cancel booking (requires auth)
   */
  async cancelBooking(bookingId: string, reason: string): Promise<Booking> {
    const response = await api.patch(`bookings/${bookingId}/cancel`, {
      cancellationReason: reason,
    });
    return response.data.data || response.data;
  }

  /**
   * Confirm booking (admin only)
   */
  async confirmBooking(bookingId: string): Promise<Booking> {
    const response = await api.patch(`bookings/${bookingId}/confirm`);
    return response.data.data || response.data;
  }

  /**
   * Complete booking (admin only)
   */
  async completeBooking(bookingId: string): Promise<Booking> {
    const response = await api.patch(`bookings/${bookingId}/complete`);
    return response.data.data || response.data;
  }

  /**
   * Delete booking (admin only)
   */
  async deleteBooking(bookingId: string): Promise<void> {
    await api.delete(`bookings/${bookingId}`);
  }

  /**
   * Get booking statistics (admin only)
   */
  async getBookingStats(venueId?: string): Promise<any> {
    const params = venueId ? `?venueId=${venueId}` : '';
    const response = await api.get(`bookings/stats${params}`);
    return response.data.data || response.data;
  }

  /**
   * Helper method to check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  /**
   * Helper method to get current token
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }
}

export const bookingsAPI = new BookingsAPI();
export default bookingsAPI;